import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReloadOutlined,
  CloseOutlined,
  SearchOutlined,
  PictureOutlined,
  LeftOutlined,
  RightOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import axios from "../../utils/axiosDash";

const STORE_STORAGE_KEY = "selectedStore";
const REFRESH_MS = 30000;
const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

// Colors used to tell stores apart in the chart and the store cards
const STORE_COLORS = [
  "#EA580C",
  "#2563EB",
  "#059669",
  "#9333EA",
  "#DB2777",
  "#0891B2",
  "#CA8A04",
  "#4F46E5",
];

const EMPTY_SUMMARY = { current: 0, total: 0, alerts: 0, cameras: 0, lastSeen: null };
const EMPTY_OPTIONS = { alertTypes: [], cameras: [], restaurants: [] };
const EMPTY_PAGINATION = { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Handles ISO strings and Mongo-export style { $date: "..." }
// NOTE: record times are shown exactly as stored (UTC), with no +5:30 shift.
const toDate = (v) => {
  if (!v) return null;
  let raw = typeof v === "object" && v.$date ? v.$date : v;
  // A string with no timezone (e.g. "2025-01-01 10:00:00") is treated as UTC
  // so the browser doesn't apply the local offset.
  if (
    typeof raw === "string" &&
    /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(raw) &&
    !/(Z|[+-]\d{2}:?\d{2})$/i.test(raw.trim())
  ) {
    raw = raw.trim().replace(" ", "T") + "Z";
  }
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

// Uses UTC getters so the date matches what is displayed
const dateKey = (d) => {
  if (!d) return "";
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${m}-${day}`;
};

const fmtTime = (d) =>
  d
    ? d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: "UTC",
      })
    : "--";

const fmtDate = (d) =>
  d
    ? d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "--";

// For the "updated at" label only: real current time in the browser's timezone
const fmtLocalTime = (d) =>
  d
    ? d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
    : "--";

const humanize = (s = "") =>
  s
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const ALERT_STYLES = {
  DIFFERENT_PERSON_ENTERED: "bg-rose-50 text-rose-700 ring-rose-600/20",
  UNAUTHORIZED_ENTRY: "bg-rose-50 text-rose-700 ring-rose-600/20",
  MULTIPLE_PEOPLE: "bg-amber-50 text-amber-800 ring-amber-600/20",
  DEFAULT: "bg-slate-100 text-slate-700 ring-slate-500/20",
};
const DOT_STYLES = {
  DIFFERENT_PERSON_ENTERED: "bg-rose-500",
  UNAUTHORIZED_ENTRY: "bg-rose-500",
  MULTIPLE_PEOPLE: "bg-amber-500",
  DEFAULT: "bg-slate-400",
};
const alertStyle = (t) => ALERT_STYLES[t] || ALERT_STYLES.DEFAULT;
const dotStyle = (t) => DOT_STYLES[t] || DOT_STYLES.DEFAULT;

// The record's time is taken only from postgresCreatedAt
const recordTime = (r) => toDate(r.postgresCreatedAt);

// Server returns { data: [...rows] }
const normalizeRows = (payload) => {
  const list = Array.isArray(payload)
    ? payload
    : payload?.data || payload?.records || payload?.result || [];
  return list.map((r) => ({ ...r, _time: recordTime(r) }));
};

const rowKey = (r, i) => r._id?.$oid || r._id || r.postgresId || i;

// Whether the current store selection is the aggregate "All Stores" view
const isAllStores = (name) => !name || name === "All Stores";

// Compact page list: 1 … 4 5 6 … 20
const buildPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set([1, total, current - 1, current, current + 1]);
  const nums = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  nums.forEach((n, i) => {
    if (i > 0 && n - nums[i - 1] > 1) out.push("gap");
    out.push(n);
  });
  return out;
};

// ---------------------------------------------------------------------------
// Small UI pieces
// ---------------------------------------------------------------------------
function StatusBadge({ item }) {
  if (item.alertGenerated) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${alertStyle(
          item.alertType
        )}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${dotStyle(item.alertType)}`} />
        {humanize(item.alertType) || "Alert"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Normal
    </span>
  );
}

function Thumb({ src, alt, onClick }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <span className="flex h-11 w-16 items-center justify-center rounded-md bg-slate-100 text-slate-400">
        <PictureOutlined />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      onClick={onClick}
      className="h-11 w-16 cursor-zoom-in rounded-md bg-slate-100 object-cover ring-1 ring-slate-200 transition-transform hover:scale-105"
    />
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="min-w-0 flex-1 px-5 py-4">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${accent || "text-slate-900"}`}>{value}</p>
      {sub && <p className="mt-0.5 truncate text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

// Chip that clearly says which store(s) the numbers below belong to
function ScopeChip({ allStores, label, count, color }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
      {allStores ? (
        <ShopOutlined className="text-slate-500" />
      ) : (
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color || "#EA580C" }} />
      )}
      {label}
      {allStores && count > 0 && <span className="text-slate-400">· {count} stores</span>}
    </span>
  );
}

// One card per store, shown only in the "All Stores" view
function StoreCard({ data, color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-w-0 rounded-xl border bg-white p-4 text-left transition-all hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
        active ? "border-orange-500 ring-1 ring-orange-500" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
          <p className="truncate text-sm font-semibold text-slate-800">{data.name}</p>
        </div>
        {data.alerts > 0 && (
          <span className="shrink-0 rounded-md bg-rose-50 px-1.5 py-0.5 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20">
            {data.alerts} alert{data.alerts > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <div>
          <p className="text-[11px] text-slate-400">Detections</p>
          <p className="text-lg font-semibold tabular-nums text-slate-800">{data.total.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-[11px] text-slate-400">Cameras</p>
          <p className="text-lg font-semibold tabular-nums text-slate-800">{data.cameras}</p>
        </div>
      </div>

      <p className="mt-2 truncate text-xs text-slate-400">
        {data.lastSeen ? `Last seen ${fmtTime(data.lastSeen)}` : "No detections yet"}
      </p>
    </button>
  );
}

// Centered image preview modal
function ImagePreview({ item, onClose }) {
  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={onClose}
        aria-label="Close preview"
        className="absolute right-4 top-4 rounded-lg bg-white/10 p-2 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
      >
        <CloseOutlined />
      </button>

      <div
        className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {item.imagePath ? (
          <img
            src={item.imagePath}
            alt="Snapshot preview"
            className="max-h-[75vh] w-auto bg-slate-100 object-contain"
          />
        ) : (
          <div className="flex h-64 w-96 items-center justify-center bg-slate-50 text-sm text-slate-400">
            No snapshot available
          </div>
        )}

        <div className="flex items-center justify-between gap-4 border-t border-slate-200 px-5 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-800">
              {fmtDate(item._time)}, {fmtTime(item._time)}
            </p>
            <p className="truncate text-xs text-slate-500">
              {item.restaurant ? `${item.restaurant} · ` : ""}
              {item.cameraIp || "--"} · Channel {item.channel ?? "--"}
            </p>
          </div>
          <StatusBadge item={item} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function StoreEntry() {
  const [storeName, setStoreName] = useState(
    localStorage.getItem(STORE_STORAGE_KEY) || "All Stores"
  );

  // Today's date (UTC, same basis as the displayed record times)
  const todayKey = dateKey(new Date());

  // Defaults to today. Empty string = all dates (if the user clears the picker).
  const [date, setDate] = useState(() => dateKey(new Date()));

  // Server data
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [storeStats, setStoreStats] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [options, setOptions] = useState(EMPTY_OPTIONS);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Filters (all applied on the server)
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [cameraFilter, setCameraFilter] = useState("ALL");
  const [restaurantFilter, setRestaurantFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [newestFirst, setNewestFirst] = useState(true);
  const [page, setPage] = useState(1);

  const [preview, setPreview] = useState(null);
  const [chartMode, setChartMode] = useState("store"); // "total" | "store" (default: By store)

  // Ignores responses from outdated requests
  const requestRef = useRef(0);

  // Whether to show the Restaurant column / filter / store breakdown
  const showRestaurant = isAllStores(storeName);

  // Debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Store changed from another tab/selector
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORE_STORAGE_KEY) {
        setStoreName(e.newValue || "All Stores");
        setRestaurantFilter("ALL");
        setStatusFilter("ALL");
        setCameraFilter("ALL");
        setPage(1);
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const fetchData = useCallback(
    async (silent = false) => {
      if (!storeName) {
        setLoading(false);
        return;
      }
      const reqId = ++requestRef.current;
      if (!silent) setRefreshing(true);
      try {
        const res = await axios.post("/get-empire-store-room", {
          store: storeName,
          page,
          limit: PAGE_SIZE,
          date,
          status: statusFilter,
          camera: cameraFilter,
          restaurant: showRestaurant ? restaurantFilter : "ALL",
          search: debouncedQuery.trim(),
          sort: newestFirst ? "desc" : "asc",
        });

        if (reqId !== requestRef.current) return; // a newer request replaced this one

        const p = res.data || {};
        setRows(normalizeRows(p));
        setPagination(p.pagination || EMPTY_PAGINATION);
        setSummary({
          ...EMPTY_SUMMARY,
          ...(p.summary || {}),
          lastSeen: toDate(p.summary?.lastSeen),
        });
        setStoreStats(
          (p.storeStats || []).map((s) => ({ ...s, lastSeen: toDate(s.lastSeen) }))
        );
        setHourly(
          (p.hourly || []).map((b) => ({
            ...b,
            label: new Date(2000, 0, 1, b.hour).toLocaleTimeString("en-IN", {
              hour: "numeric",
              hour12: true,
            }),
          }))
        );
        setOptions({ ...EMPTY_OPTIONS, ...(p.options || {}) });
        setError("");
        setLastUpdated(new Date());
      } catch (e) {
        if (reqId !== requestRef.current) return;
        setError(e?.response?.data?.message || "Could not load store room data. Try again.");
      } finally {
        if (reqId === requestRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [
      storeName,
      page,
      date,
      statusFilter,
      cameraFilter,
      restaurantFilter,
      showRestaurant,
      debouncedQuery,
      newestFirst,
    ]
  );

  // Fetch whenever page / filters / store change, and poll in the background
  useEffect(() => {
    setLoading(true);
    fetchData();
    const id = setInterval(() => fetchData(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchData]);

  // ---- Filter handlers: every change goes back to page 1 ----
  const changeDate = (v) => {
    setDate(v);
    setPage(1);
  };
  const changeStatus = (v) => {
    setStatusFilter(v);
    setPage(1);
  };
  const changeCamera = (v) => {
    setCameraFilter(v);
    setPage(1);
  };
  const changeRestaurant = (v) => {
    setRestaurantFilter(v);
    setPage(1);
  };
  const changeQuery = (v) => {
    setQuery(v);
    setPage(1);
  };
  const toggleSort = () => {
    setNewestFirst((v) => !v);
    setPage(1);
  };

  // Stable color per store
  const restaurants = options.restaurants;
  const storeColor = useMemo(() => {
    const map = {};
    restaurants.forEach((name, i) => {
      map[name] = STORE_COLORS[i % STORE_COLORS.length];
    });
    return map;
  }, [restaurants]);

  // Is the summary showing every store, or just one picked from the store cards / dropdown?
  const focusedStore = showRestaurant && restaurantFilter !== "ALL" ? restaurantFilter : null;
  const summaryAllStores = showRestaurant && !focusedStore;

  const stats = summary;

  // Can the chart be split by store? (depends only on the page's store view,
  // never on which store card is selected)
  const canSplitByStore = showRestaurant && restaurants.length > 1;
  const effectiveChartMode = canSplitByStore ? chartMode : "total";

  // Pagination numbers (from the server)
  const totalRows = pagination.total;
  const totalPages = Math.max(1, pagination.totalPages);
  const safePage = pagination.page;
  const startIdx = totalRows === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(safePage * PAGE_SIZE, totalRows);
  const pageList = buildPageList(safePage, totalPages);

  // Today is the default date, so only a different date (or "all dates") counts as a filter
  const hasFilters =
    date !== todayKey ||
    statusFilter !== "ALL" ||
    cameraFilter !== "ALL" ||
    (showRestaurant && restaurantFilter !== "ALL") ||
    query;

  const clearFilters = () => {
    setDate(dateKey(new Date())); // back to today
    setStatusFilter("ALL");
    setCameraFilter("ALL");
    setRestaurantFilter("ALL");
    setQuery("");
    setDebouncedQuery("");
    setPage(1);
  };

  const closePreview = useCallback(() => setPreview(null), []);

  // Column count depends on whether the restaurant column is visible.
  // Used for the loading skeleton and empty-state row.
  const colCount = showRestaurant ? 8 : 7;

  // ---- No store selected ----
  if (!storeName) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="max-w-sm text-center">
          <p className="text-base font-semibold text-slate-800">No store selected</p>
          <p className="mt-1 text-sm text-slate-500">
            Choose a store from the store selector to see its entry activity.
          </p>
        </div>
      </div>
    );
  }

  const selectCls =
    "rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500";

  // Text describing the scope of the summary cards
  const scopeLabel = summaryAllStores ? "All Stores" : focusedStore || storeName;
  const scopeColor = focusedStore ? storeColor[focusedStore] : undefined;
  const peopleSub = summaryAllStores
    ? `Sum of ${storeStats.length} store${storeStats.length === 1 ? "" : "s"}${
        stats.lastSeen ? ` · last seen ${fmtTime(stats.lastSeen)}` : ""
      }`
    : stats.lastSeen
    ? `Last seen ${fmtTime(stats.lastSeen)}`
    : "No detections yet";

  // Label for the graph (independent of the selected store card)
  const chartScopeLabel = showRestaurant ? "All Stores" : storeName;

  return (
    <div className="min-h-screen w-full bg-white text-slate-800">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">Store Room Entry</h1>
            <p className="mt-1 text-sm text-slate-500">
              Store room activity at <span className="font-medium text-slate-700">{storeName}</span>
              {lastUpdated && <span className="text-slate-400">, updated {fmtLocalTime(lastUpdated)}</span>}
            </p>
          </div>

          {/* Date filter + Refresh, side by side */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={date}
              max={todayKey}
              onChange={(e) => changeDate(e.target.value)}
              className={selectCls}
              aria-label="Filter by date"
            />
            <button
              onClick={() => fetchData()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-3.5 py-2 text-sm font-medium text-white hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
            >
              <ReloadOutlined spin={refreshing} />
              Refresh
            </button>
          </div>
        </header>

        {error && (
          <div className="mb-5 flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span>{error}</span>
            <button onClick={() => fetchData()} className="font-medium underline">
              Try again
            </button>
          </div>
        )}

        {/* Scope bar: tells the user whose data the summary cards show */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Showing data for</span>
          <ScopeChip
            allStores={summaryAllStores}
            label={scopeLabel}
            count={storeStats.length}
            color={scopeColor}
          />
          {date && <span className="text-xs text-slate-500">on {fmtDate(toDate(`${date}T00:00:00Z`))}</span>}
          {focusedStore && (
            <button
              onClick={() => changeRestaurant("ALL")}
              className="text-xs font-medium text-orange-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
            >
              Back to all stores
            </button>
          )}
        </div>

        {/* Summary strip */}
        <section className="mb-6 overflow-hidden rounded-xl border border-slate-200">
          <div className="grid grid-cols-2 divide-x divide-slate-200 lg:grid-cols-4">
            <Stat
              label={summaryAllStores ? "People in rooms now (all stores)" : "People in room now"}
              value={stats.current}
              sub={peopleSub}
              accent="text-orange-600"
            />
            <Stat
              label="Total detections"
              value={stats.total.toLocaleString()}
              sub={summaryAllStores ? "All stores combined" : scopeLabel}
            />
            <div className="border-t border-slate-200 lg:border-t-0">
              <Stat
                label="Alerts generated"
                value={stats.alerts.toLocaleString()}
                sub={summaryAllStores ? "All stores combined" : scopeLabel}
                accent="text-rose-600"
              />
            </div>
            <div className="border-t border-slate-200 lg:border-t-0">
              <Stat
                label="Active Cameras"
                value={stats.cameras}
                sub={summaryAllStores ? "All stores combined" : scopeLabel}
              />
            </div>
          </div>
        </section>

        {/* Store breakdown: only in the All Stores view */}
        {showRestaurant && storeStats.length > 0 && (
          <section className="mb-6">
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-800">Store Breakdown</h2>
              <p className="text-xs text-slate-400">Click a store to focus the summary cards and table on it</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {storeStats.map((s) => (
                <StoreCard
                  key={s.name}
                  data={s}
                  color={storeColor[s.name] || "#94A3B8"}
                  active={restaurantFilter === s.name}
                  onClick={() => changeRestaurant(restaurantFilter === s.name ? "ALL" : s.name)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Entries by hour graph: sits below the store breakdown, always shows every store */}
        <section className="mb-6 overflow-hidden rounded-xl border border-slate-200">
          <div className="px-5 pb-3 pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-medium text-slate-500">Entries by Hour</p>
                <span className="text-xs text-slate-300">·</span>
                <p className="text-xs text-slate-400">
                  {effectiveChartMode === "store" ? "Split by Store" : `${chartScopeLabel}, total`}
                </p>
              </div>

              {canSplitByStore && (
                <div className="inline-flex rounded-lg bg-slate-100 p-0.5 text-xs font-medium" role="group" aria-label="Chart view">
                  {[
                    { id: "store", label: "By Store" },
                    { id: "total", label: "Total" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setChartMode(opt.id)}
                      aria-pressed={chartMode === opt.id}
                      className={`rounded-md px-3 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                        chartMode === opt.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className={effectiveChartMode === "store" ? "h-56" : "h-40"}>
              {hourly.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-400">
                  No entries recorded.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={hourly} margin={{ top: 8, right: 4, left: -28, bottom: 0 }}>
                    <defs>
                      <linearGradient id="detGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EA580C" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#EA580C" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />

                    {effectiveChartMode === "store" ? (
                      <>
                        <Legend
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ fontSize: 12, paddingTop: 4 }}
                        />
                        {restaurants.map((name) => (
                          <Area
                            key={name}
                            type="monotone"
                            dataKey={name}
                            name={name}
                            stroke={storeColor[name]}
                            fill={storeColor[name]}
                            fillOpacity={0.08}
                            strokeWidth={2}
                          />
                        ))}
                      </>
                    ) : (
                      <>
                        <Area
                          type="monotone"
                          dataKey="detections"
                          name={showRestaurant ? "Detections (all stores)" : "Detections"}
                          stroke="#EA580C"
                          fill="url(#detGrad)"
                          strokeWidth={2}
                        />
                        <Area
                          type="monotone"
                          dataKey="alerts"
                          name={showRestaurant ? "Alerts (all stores)" : "Alerts"}
                          stroke="#E11D48"
                          fill="none"
                          strokeWidth={2}
                        />
                      </>
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </section>

        {/* Toolbar */}
        <section className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-xs">
            <SearchOutlined className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => changeQuery(e.target.value)}
              placeholder={showRestaurant ? "Search message, camera or restaurant" : "Search message or camera"}
              className={`${selectCls} w-full pl-9`}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={statusFilter} onChange={(e) => changeStatus(e.target.value)} className={selectCls}>
              <option value="ALL">All statuses</option>
              {options.alertTypes.map((t) => (
                <option key={t} value={t}>
                  {humanize(t)}
                </option>
              ))}
            </select>
            {showRestaurant && restaurants.length > 1 && (
              <select
                value={restaurantFilter}
                onChange={(e) => changeRestaurant(e.target.value)}
                className={selectCls}
                aria-label="Filter by restaurant"
              >
                <option value="ALL">All Restaurants</option>
                {restaurants.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
            {options.cameras.length > 1 && (
              <select value={cameraFilter} onChange={(e) => changeCamera(e.target.value)} className={selectCls}>
                <option value="ALL">All Cameras</option>
                {options.cameras.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            )}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="rounded-lg px-3 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                Clear filters
              </button>
            )}
          </div>
        </section>

        {/* Table */}
        <section className="overflow-hidden rounded-xl border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse text-left text-sm">
              <thead className="bg-slate-50 text-xs font-medium text-slate-500">
                <tr>
                  <th scope="col" className="px-4 py-3">Snapshot</th>
                  <th scope="col" className="px-4 py-3">
                    <button
                      onClick={toggleSort}
                      className="inline-flex items-center gap-1.5 font-medium hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                      aria-label={`Sort by time, ${newestFirst ? "newest" : "oldest"} first`}
                    >
                      Date and time
                      {newestFirst ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
                    </button>
                  </th>
                  {showRestaurant && (
                    <th scope="col" className="px-4 py-3">Restaurant</th>
                  )}
                  <th scope="col" className="px-4 py-3">Status</th>
                  <th scope="col" className="px-4 py-3">Message</th>
                  <th scope="col" className="px-4 py-3">Camera</th>
                  <th scope="col" className="px-4 py-3 text-right">Channel</th>
                  <th scope="col" className="px-4 py-3 text-right">People</th>
                </tr>
              </thead>
              <tbody
                className={`divide-y divide-slate-100 transition-opacity ${
                  refreshing && !loading ? "opacity-60" : "opacity-100"
                }`}
              >
                {loading ? (
                  [0, 1, 2, 3, 4].map((i) => (
                    <tr key={i}>
                      <td colSpan={colCount} className="px-4 py-3">
                        <div className="h-11 animate-pulse rounded-md bg-slate-100" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="px-4 py-14 text-center">
                      <p className="text-sm font-medium text-slate-700">No Entries Found</p>
                      <p className="mt-1 text-sm text-slate-400">Try a different date or clear the filters.</p>
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr
                      key={rowKey(r, i)}
                      className={`transition-colors hover:bg-orange-50/40 ${
                        r.alertGenerated ? "bg-rose-50/30" : ""
                      }`}
                    >
                      <td className="px-4 py-2.5">
                        <Thumb
                          src={r.imagePath}
                          alt={r.alertType ? humanize(r.alertType) : "Store room snapshot"}
                          onClick={() => setPreview(r)}
                        />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5">
                        <p className="font-medium tabular-nums text-slate-800">{fmtTime(r._time)}</p>
                        <p className="text-xs text-slate-400">{fmtDate(r._time)}</p>
                      </td>
                      {showRestaurant && (
                        <td className="whitespace-nowrap px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                            {r.restaurant && (
                              <span
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ backgroundColor: storeColor[r.restaurant] || "#94A3B8" }}
                              />
                            )}
                            {r.restaurant || "—"}
                          </span>
                        </td>
                      )}
                      <td className="px-4 py-2.5">
                        <StatusBadge item={r} />
                      </td>
                      <td className="max-w-xs px-4 py-2.5">
                        <p className="line-clamp-2 text-slate-600">
                          {r.alertMessage || "Person detected in the store room."}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{r.cameraIp || "--"}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600">{r.channel ?? "--"}</td>
                      <td className="px-4 py-2.5 text-right font-medium tabular-nums text-slate-800">
                        {r.currentPeopleCount ?? 0}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 sm:flex-row">
            <p>
              {totalRows === 0
                ? "0 entries"
                : `Showing ${startIdx}-${endIdx} of ${totalRows.toLocaleString()} entries`}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              <button
                onClick={() => setPage(Math.max(1, safePage - 1))}
                disabled={safePage <= 1 || loading}
                aria-label="Previous page"
                className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <LeftOutlined />
              </button>

              {pageList.map((p, i) =>
                p === "gap" ? (
                  <span key={`gap-${i}`} className="px-1 text-slate-400">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    disabled={loading}
                    aria-label={`Page ${p}`}
                    aria-current={p === safePage ? "page" : undefined}
                    className={`min-w-[2rem] rounded-lg border px-2 py-1.5 text-sm tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
                      p === safePage
                        ? "border-orange-500 bg-orange-50 font-medium text-orange-700"
                        : "border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage >= totalPages || loading}
                aria-label="Next page"
                className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <RightOutlined />
              </button>
            </div>
          </div>
        </section>
      </div>

      <ImagePreview item={preview} onClose={closePreview} />
    </div>
  );
}